import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, of, OperatorFunction, from } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { produce } from 'immer';
import { toReduxError, type ReduxError } from '@shared/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { TypedEpic } from '../types';
import { FrontPageType } from '../../../../types/frontPageType';

// Actions
export enum Actions {
  GET_FRONT_PAGE = 'GET_FRONT_PAGE',
  GET_FRONT_PAGE_SUCCESS = 'GET_FRONT_PAGE_SUCCESS',
  GET_FRONT_PAGE_FAIL = 'GET_FRONT_PAGE_FAIL',
  GET_FRONT_PAGE_CANCEL = 'GET_FRONT_PAGE_CANCEL',
}

export interface State {
  data?: FrontPageType;
  meta: { isUpdating: boolean; fetchedAt?: string; isLoggedin?: boolean };
  error?: ReduxError;
}

export const initialState: State = {
  meta: {
    isUpdating: false,
  },
};

export const actions = {
  getFrontPage: createAsyncAction(
    Actions.GET_FRONT_PAGE, // request payload creator
    Actions.GET_FRONT_PAGE_SUCCESS, // success payload creator
    Actions.GET_FRONT_PAGE_FAIL, // failure payload creator
    Actions.GET_FRONT_PAGE_CANCEL, // optional cancel payload creator
  )<[undefined, { token?: string; abTestWeight?: number }], FrontPageType, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getFrontPage.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.meta = {
        isUpdating: true,
        fetchedAt: draftState.meta.fetchedAt || undefined,
        isLoggedin: !!action.meta.token,
      };
    }),
  )
  .handleAction(actions.getFrontPage.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.meta = {
        isUpdating: false,
        fetchedAt: new Date().toISOString(),
        isLoggedin: state.meta.isLoggedin,
      };
      draftState.data = action.payload;
    }),
  )
  .handleAction(actions.getFrontPage.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.meta = { isUpdating: false };
      draftState.error = action.payload;
    }),
  )
  .handleAction(actions.getFrontPage.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.meta = { isUpdating: false };
    }),
  );

export const fetchDocumentEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_FRONT_PAGE),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getFrontPage.request>[]
    >,
    switchMap(([action]) => {
      const abTestWeight = action.meta?.abTestWeight?.toString();

      const headers: Record<string, string> = {
        credentials: 'include',
        ...(abTestWeight && { 'X-Ab-Test-Weight': abTestWeight }),
      };

      return from(
        apiClient.get<FrontPageType>(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].frontpage,
          headers,
        }),
      ).pipe(
        map((response) => actions.getFrontPage.success(response.data)),
        catchError((err) => of(actions.getFrontPage.failure(toReduxError(err, 'Could not get document')))),
      );
    }),
  );

export const epics: TypedEpic[] = [fetchDocumentEpic];
